import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  ShieldCheck,
  Wallet,
  Users,
  LineChart,
  Lock,
  ArrowRight,
  Sparkles,
  BadgeCheck,
  Layers,
  Headphones,
  ChevronRight,
  CheckCircle2,
  TrendingUp,
  Star,
  Send,
  Fingerprint,
  Globe2,
} from "lucide-react";
import "./App.css";

/**
 * T-VENTURES — Public landing page.
 * Rendered at "/". Users navigate to /login.html or /register.html for the app.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.2, 0.65, 0.3, 1], delay: i * 0.06 },
  }),
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

function useSignedInRedirect() {
  const [loggedIn, setLoggedIn] = useState(false);
  useEffect(() => {
    fetch(`${BACKEND}/api/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.user) setLoggedIn(true); })
      .catch(() => {});
  }, []);
  return loggedIn;
}

function Navbar({ loggedIn }) {
  return (
    <header
      data-testid="landing-navbar"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-white/70 border-b border-emerald-100/70"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2.5" data-testid="brand-link">
          <span className="grid place-items-center w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-lg shadow-emerald-500/25">
            <img src="/tventures-logo.svg" alt="" className="w-5 h-5" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="tracking-[0.14em] font-extrabold text-emerald-900 text-[15px]">
              T-VENTURES
            </span>
            <span className="text-[9px] tracking-[0.28em] text-emerald-600 font-semibold mt-0.5">
              SECURE FINTECH
            </span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8 text-[13px] font-medium text-emerald-900/80">
          <a href="#features" className="hover:text-emerald-700 transition-colors" data-testid="nav-features">Features</a>
          <a href="#plans" className="hover:text-emerald-700 transition-colors" data-testid="nav-plans">Plans</a>
          <a href="#how" className="hover:text-emerald-700 transition-colors" data-testid="nav-how">How it works</a>
          <a href="#security" className="hover:text-emerald-700 transition-colors" data-testid="nav-security">Security</a>
        </nav>

        <div className="flex items-center gap-2.5">
          {loggedIn ? (
            <a
              href="/dashboard.html"
              data-testid="nav-dashboard"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] font-semibold shadow-md shadow-emerald-500/25 transition-colors"
            >
              Dashboard <ArrowRight className="w-4 h-4" />
            </a>
          ) : (
            <>
              <a
                href="/login.html"
                data-testid="nav-signin"
                className="hidden sm:inline-flex items-center px-4 py-2 rounded-full text-[13px] font-semibold text-emerald-800 hover:bg-emerald-50 transition-colors"
              >
                Sign in
              </a>
              <a
                href="/register.html"
                data-testid="nav-signup"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] font-semibold shadow-md shadow-emerald-500/25 transition-colors"
              >
                Get started <ArrowRight className="w-4 h-4" />
              </a>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function Hero() {
  const reduce = useReducedMotion();
  return (
    <section className="relative pt-32 pb-24 sm:pt-40 sm:pb-32 overflow-hidden" data-testid="landing-hero">
      {/* Animated backdrop orbs */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full bg-gradient-to-br from-emerald-200/70 via-emerald-100/50 to-transparent blur-3xl animate-[floatA_18s_ease-in-out_infinite]" />
        <div className="absolute top-40 -right-24 w-[460px] h-[460px] rounded-full bg-gradient-to-br from-teal-200/70 to-transparent blur-3xl animate-[floatB_22s_ease-in-out_infinite]" />
        <div className="absolute bottom-0 left-1/3 w-[380px] h-[380px] rounded-full bg-gradient-to-br from-lime-200/50 to-transparent blur-3xl animate-[floatA_26s_ease-in-out_infinite]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0)_0%,rgba(255,255,255,1)_75%)]" />
      </div>

      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial="hidden"
          animate="show"
          variants={stagger}
          className="grid lg:grid-cols-12 gap-12 items-center"
        >
          <div className="lg:col-span-7">
            <motion.span
              variants={fadeUp}
              className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white/70 backdrop-blur px-3.5 py-1.5 text-[11px] font-semibold text-emerald-800 shadow-sm"
              data-testid="hero-eyebrow"
            >
              <Sparkles className="w-3.5 h-3.5" />
              A smarter way to grow your capital
            </motion.span>

            <motion.h1
              variants={fadeUp}
              className="mt-6 font-black tracking-tight text-emerald-950 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
              style={{ fontFamily: "'Sora','Plus Jakarta Sans',system-ui,sans-serif" }}
              data-testid="hero-title"
            >
              Grow your portfolio with{" "}
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 bg-clip-text text-transparent">
                  structured plans
                </span>
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none">
                  <path d="M2 8 C 80 2, 220 2, 298 8" stroke="url(#g1)" strokeWidth="3" strokeLinecap="round" />
                  <defs>
                    <linearGradient id="g1" x1="0" x2="1">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#14b8a6" />
                    </linearGradient>
                  </defs>
                </svg>
              </span>
              , transparent reporting and a rewarding referral network.
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mt-6 text-base sm:text-lg text-emerald-900/70 max-w-xl leading-relaxed"
              data-testid="hero-sub"
            >
              T-VENTURES gives you a modern, mobile-first workspace to deposit,
              track and diversify across curated investment plans — with
              tier-based referral rewards on every funded deposit.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="/register.html"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-lg shadow-emerald-500/30 transition-all hover:-translate-y-0.5"
              >
                Open free account
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href="/login.html"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white hover:bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold transition-colors"
              >
                <Lock className="w-4 h-4" /> Sign in
              </a>
            </motion.div>

            <motion.div variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-6 text-[12px] text-emerald-900/70">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Encrypted end-to-end</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> KYC-verified accounts</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Instant deposits</div>
            </motion.div>
          </div>

          {/* Hero visual — dashboard mock */}
          <motion.div
            variants={fadeUp}
            className="lg:col-span-5 relative"
            data-testid="hero-visual"
          >
            <motion.div
              animate={reduce ? {} : { y: [0, -10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="relative mx-auto max-w-md rounded-3xl border border-emerald-100 bg-white/80 backdrop-blur-xl p-5 shadow-[0_30px_80px_-20px_rgba(6,95,70,0.25)]"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] tracking-widest text-emerald-600 font-bold">PORTFOLIO</div>
                  <div className="text-2xl font-black text-emerald-950 mt-0.5">$ 24,860.50</div>
                  <div className="text-[11px] text-emerald-600 font-semibold mt-1 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> +$ 1,240 this cycle
                  </div>
                </div>
                <div className="w-10 h-10 rounded-xl bg-emerald-100 grid place-items-center">
                  <Wallet className="w-5 h-5 text-emerald-700" />
                </div>
              </div>

              {/* Mini chart */}
              <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 p-3">
                <svg viewBox="0 0 300 80" className="w-full h-16">
                  <defs>
                    <linearGradient id="c" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity="0.45" />
                      <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d="M0 60 L 30 55 L 60 48 L 90 52 L 120 40 L 150 44 L 180 32 L 210 36 L 240 22 L 270 26 L 300 12 L 300 80 L 0 80 Z" fill="url(#c)" />
                  <path d="M0 60 L 30 55 L 60 48 L 90 52 L 120 40 L 150 44 L 180 32 L 210 36 L 240 22 L 270 26 L 300 12" stroke="#059669" strokeWidth="2" fill="none" strokeLinecap="round" />
                </svg>
                <div className="flex justify-between text-[10px] text-emerald-600/70 font-medium mt-1">
                  <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span><span>Sun</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 mt-4">
                {[
                  { l: "Active plans", v: "3" },
                  { l: "Referrals", v: "12" },
                  { l: "KYC", v: "Verified" },
                ].map((s) => (
                  <div key={s.l} className="rounded-xl border border-emerald-100 bg-white p-2.5 text-center">
                    <div className="text-[10px] text-emerald-700/70 font-semibold uppercase tracking-wider">{s.l}</div>
                    <div className="text-sm font-extrabold text-emerald-950 mt-0.5">{s.v}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Floating badges */}
            <motion.div
              animate={reduce ? {} : { y: [0, 8, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -left-4 top-8 hidden sm:flex items-center gap-2 rounded-2xl bg-white shadow-lg border border-emerald-100 px-3 py-2"
            >
              <div className="w-8 h-8 rounded-full bg-emerald-100 grid place-items-center"><BadgeCheck className="w-4 h-4 text-emerald-700" /></div>
              <div>
                <div className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider">Approved</div>
                <div className="text-xs text-emerald-950 font-bold">$500 deposit</div>
              </div>
            </motion.div>

            <motion.div
              animate={reduce ? {} : { y: [0, -12, 0] }}
              transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
              className="absolute -right-2 bottom-6 hidden sm:flex items-center gap-2 rounded-2xl bg-white shadow-lg border border-emerald-100 px-3 py-2"
            >
              <div className="w-8 h-8 rounded-full bg-emerald-100 grid place-items-center"><Users className="w-4 h-4 text-emerald-700" /></div>
              <div>
                <div className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider">Referral</div>
                <div className="text-xs text-emerald-950 font-bold">+$ 48.20 earned</div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function TrustStrip() {
  const items = [
    { k: "Active users", v: "42,000+", icon: Users },
    { k: "Volume processed", v: "$18M+", icon: Wallet },
    { k: "Countries served", v: "35+", icon: Globe2 },
    { k: "Uptime (12 mo)", v: "99.98%", icon: ShieldCheck },
  ];
  return (
    <section className="py-10 border-y border-emerald-100 bg-white/60 backdrop-blur" data-testid="trust-strip">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map(({ k, v, icon: Icon }, i) => (
          <motion.div
            key={k}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ delay: i * 0.08 }}
            className="flex items-center gap-3"
            data-testid={`trust-${k.replace(/\s/g, "-").toLowerCase()}`}
          >
            <div className="w-11 h-11 rounded-xl bg-emerald-50 grid place-items-center">
              <Icon className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <div className="text-xl font-black text-emerald-950 leading-none">{v}</div>
              <div className="text-[11px] text-emerald-800/70 mt-1 font-semibold uppercase tracking-wider">{k}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function Features() {
  const features = [
    { icon: Layers, title: "Curated investment plans", desc: "Choose from carefully-structured plans built for different capital ranges and time horizons." },
    { icon: Users, title: "3-tier referral rewards", desc: "Grow a downline and earn commissions on every funded deposit across three tiers." },
    { icon: LineChart, title: "Live portfolio reporting", desc: "Watch cycle payouts, referral earnings and plan progress update in real-time." },
    { icon: Wallet, title: "Multiple deposit channels", desc: "Fund via bank transfer, EasyPaisa, JazzCash and other admin-configured options." },
    { icon: ShieldCheck, title: "Bank-grade security", desc: "TLS everywhere, hashed credentials, lockouts on abuse and JWT session control." },
    { icon: Headphones, title: "Human support, 24/7", desc: "Reach the team via in-app tickets, email or WhatsApp — real people, fast answers." },
  ];
  return (
    <section id="features" className="py-24 sm:py-32" data-testid="section-features">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-100 px-3 py-1 text-[11px] font-bold text-emerald-700 uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" /> What you get
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-black text-emerald-950 tracking-tight" style={{ fontFamily: "'Sora','Plus Jakarta Sans',system-ui,sans-serif" }}>
            A complete workspace for the modern investor.
          </h2>
          <p className="mt-4 text-emerald-900/70 text-base">
            Everything you need to fund, allocate and track a diversified portfolio — packaged into one focused mobile-first experience.
          </p>
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
          className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {features.map(({ icon: Icon, title, desc }, i) => (
            <motion.div
              key={title}
              variants={fadeUp}
              custom={i}
              className="group relative rounded-2xl border border-emerald-100 bg-white p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-emerald-200 transition-all duration-300"
              data-testid={`feature-${i}`}
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-50 grid place-items-center border border-emerald-200/70 group-hover:from-emerald-500 group-hover:to-emerald-700 transition-all">
                <Icon className="w-6 h-6 text-emerald-700 group-hover:text-white transition-colors" />
              </div>
              <h3 className="mt-5 text-lg font-extrabold text-emerald-950">{title}</h3>
              <p className="mt-2 text-sm text-emerald-900/70 leading-relaxed">{desc}</p>
              <div className="absolute inset-0 pointer-events-none rounded-2xl ring-1 ring-inset ring-transparent group-hover:ring-emerald-200 transition-colors" />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function PlansPreview() {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    fetch(`${BACKEND}/api/plans`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => setPlans((d && d.items) || []))
      .catch(() => setPlans([]));
  }, []);

  return (
    <section id="plans" className="relative py-24 sm:py-32 bg-gradient-to-b from-emerald-50/70 via-white to-white" data-testid="section-plans">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-white border border-emerald-100 px-3 py-1 text-[11px] font-bold text-emerald-700 uppercase tracking-wider">
              <Layers className="w-3.5 h-3.5" /> Plans
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-black text-emerald-950 tracking-tight" style={{ fontFamily: "'Sora','Plus Jakarta Sans',system-ui,sans-serif" }}>
              Pick a strategy that fits your goals.
            </h2>
            <p className="mt-4 text-emerald-900/70 text-base">
              Each plan is a structured allocation with a target ROI and duration. All plans are managed by the T-VENTURES team and reported transparently in your dashboard.
            </p>
          </div>
          <a href="/plans.html" className="hidden md:inline-flex items-center gap-1.5 text-emerald-700 hover:text-emerald-900 font-semibold text-sm" data-testid="plans-view-all">
            View all plans <ChevronRight className="w-4 h-4" />
          </a>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {(plans.length ? plans : [
            { name: "Starter", min_amount: 1000, max_amount: 9999, roi_percent: 8, duration_days: 30, is_popular: false, description: "Perfect for beginners" },
            { name: "Gold", min_amount: 10000, max_amount: 99999, roi_percent: 14, duration_days: 90, is_popular: true, description: "Most popular plan" },
            { name: "Platinum", min_amount: 100000, max_amount: 999999, roi_percent: 22, duration_days: 180, is_popular: false, description: "Dedicated manager included" },
          ]).slice(0, 3).map((p, i) => (
            <motion.div
              key={p.name || p.id || i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08 }}
              className={`relative rounded-2xl p-6 border ${p.is_popular ? "border-emerald-500 bg-gradient-to-br from-emerald-600 to-emerald-800 text-white shadow-2xl shadow-emerald-600/25" : "border-emerald-100 bg-white text-emerald-950 shadow-sm"} transition-transform hover:-translate-y-1 duration-300`}
              data-testid={`plan-card-${i}`}
            >
              {p.is_popular && (
                <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-white text-emerald-700 text-[10px] font-black uppercase tracking-wider px-2.5 py-1 shadow">
                  <Star className="w-3 h-3 fill-current" /> Most chosen
                </span>
              )}
              <div className={`text-[11px] font-bold uppercase tracking-widest ${p.is_popular ? "text-emerald-100" : "text-emerald-600"}`}>
                {p.name}
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-5xl font-black">{p.roi_percent}%</span>
                <span className={`text-sm font-semibold ${p.is_popular ? "text-emerald-100" : "text-emerald-700/70"}`}>target ROI</span>
              </div>
              <div className={`text-sm mt-1 ${p.is_popular ? "text-emerald-100" : "text-emerald-800/70"}`}>
                over {p.duration_days} days
              </div>
              <div className={`h-px my-5 ${p.is_popular ? "bg-white/25" : "bg-emerald-100"}`} />
              <ul className={`space-y-2.5 text-sm ${p.is_popular ? "text-emerald-50" : "text-emerald-900/80"}`}>
                <li className="flex items-center gap-2"><CheckCircle2 className={`w-4 h-4 ${p.is_popular ? "text-emerald-200" : "text-emerald-600"}`} /> Min entry: $ {Number(p.min_amount || 0).toLocaleString()}</li>
                <li className="flex items-center gap-2"><CheckCircle2 className={`w-4 h-4 ${p.is_popular ? "text-emerald-200" : "text-emerald-600"}`} /> Cycle payouts</li>
                <li className="flex items-center gap-2"><CheckCircle2 className={`w-4 h-4 ${p.is_popular ? "text-emerald-200" : "text-emerald-600"}`} /> Referral rewards eligible</li>
                <li className="flex items-center gap-2"><CheckCircle2 className={`w-4 h-4 ${p.is_popular ? "text-emerald-200" : "text-emerald-600"}`} /> Cancel anytime after maturity</li>
              </ul>
              <a
                href="/register.html"
                data-testid={`plan-cta-${i}`}
                className={`mt-6 inline-flex w-full items-center justify-center gap-1.5 rounded-full py-2.5 text-sm font-bold transition-colors ${p.is_popular ? "bg-white text-emerald-800 hover:bg-emerald-50" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
              >
                Start with {p.name} <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
          ))}
        </div>
        <p className="mt-8 text-center text-[11px] text-emerald-900/50 max-w-2xl mx-auto">
          All figures shown are targeted, not guaranteed. Every investment carries market and operational risk. Read the full plan brief before allocating capital.
        </p>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { icon: Fingerprint, title: "Create your account", desc: "Sign up in under 60 seconds with your phone number and a referral code." },
    { icon: Wallet, title: "Fund your wallet", desc: "Deposit via bank, EasyPaisa or JazzCash — upload your slip, we do the rest." },
    { icon: Layers, title: "Choose a plan", desc: "Pick a structured plan that matches your capital and time horizon." },
    { icon: TrendingUp, title: "Track & withdraw", desc: "Monitor cycle payouts and request withdrawals on the days set by admin." },
  ];
  return (
    <section id="how" className="py-24 sm:py-32" data-testid="section-how">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-100 px-3 py-1 text-[11px] font-bold text-emerald-700 uppercase tracking-wider">
            <Send className="w-3.5 h-3.5" /> How it works
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-black text-emerald-950 tracking-tight" style={{ fontFamily: "'Sora','Plus Jakarta Sans',system-ui,sans-serif" }}>
            Get started in four simple steps.
          </h2>
          <p className="mt-4 text-emerald-900/70">
            From your first deposit to your first payout — everything is guided and transparent.
          </p>
        </div>

        <div className="mt-16 grid md:grid-cols-4 gap-5 relative">
          <div aria-hidden className="hidden md:block absolute left-8 right-8 top-8 h-px bg-gradient-to-r from-transparent via-emerald-200 to-transparent" />
          {steps.map(({ icon: Icon, title, desc }, i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.1 }}
              className="relative rounded-2xl bg-white border border-emerald-100 p-6 shadow-sm hover:shadow-lg transition-shadow"
              data-testid={`how-step-${i}`}
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center text-white shadow-lg shadow-emerald-500/25">
                <Icon className="w-7 h-7" />
              </div>
              <div className="absolute top-4 right-4 text-6xl font-black text-emerald-100 leading-none select-none">0{i + 1}</div>
              <h3 className="mt-5 text-lg font-extrabold text-emerald-950">{title}</h3>
              <p className="mt-2 text-sm text-emerald-900/70 leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Security() {
  const items = [
    { icon: Lock, title: "Encrypted end-to-end", desc: "All traffic is TLS 1.3. Credentials are stored as bcrypt hashes — never in plain text." },
    { icon: ShieldCheck, title: "Session guardrails", desc: "JWT rotation, brute-force lockouts and cookie-scoped tokens keep your session safe." },
    { icon: BadgeCheck, title: "KYC before payouts", desc: "Identity verification is required for all withdrawals so accounts stay yours alone." },
  ];
  return (
    <section id="security" className="relative py-24 sm:py-32 overflow-hidden bg-gradient-to-br from-emerald-950 via-emerald-900 to-emerald-800" data-testid="section-security">
      <div aria-hidden className="absolute inset-0 opacity-20 pointer-events-none [background-image:radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.4)_1px,transparent_0)] [background-size:24px_24px]" />
      <div aria-hidden className="absolute -top-24 -left-24 w-[500px] h-[500px] rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
      <div aria-hidden className="absolute -bottom-24 -right-24 w-[560px] h-[560px] rounded-full bg-teal-400/10 blur-3xl pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-800/60 border border-emerald-500/30 backdrop-blur px-3 py-1 text-[11px] font-bold text-emerald-100 uppercase tracking-wider">
            <ShieldCheck className="w-3.5 h-3.5" /> Security first
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight" style={{ fontFamily: "'Sora','Plus Jakarta Sans',system-ui,sans-serif" }}>
            Your capital is protected at every layer.
          </h2>
          <p className="mt-4 text-emerald-100/80 leading-relaxed">
            We design like a bank and iterate like a startup. Cryptographic session tokens, immutable audit trails and human review on every payout keep operations tight and predictable.
          </p>
          <a href="/register.html" data-testid="security-cta" className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-emerald-800 hover:bg-emerald-50 font-semibold transition-colors">
            Create your account <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        <div className="lg:col-span-7 space-y-3">
          {items.map(({ icon: Icon, title, desc }, i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.1 }}
              className="rounded-2xl border border-emerald-500/20 bg-white/5 backdrop-blur p-5 flex items-start gap-4 hover:border-emerald-500/40 hover:bg-white/10 transition-colors"
              data-testid={`security-item-${i}`}
            >
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 border border-emerald-400/30 grid place-items-center shrink-0">
                <Icon className="w-6 h-6 text-emerald-300" />
              </div>
              <div>
                <h3 className="text-lg font-extrabold text-white">{title}</h3>
                <p className="mt-1 text-sm text-emerald-100/70 leading-relaxed">{desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const quotes = [
    { name: "Ayesha R.", role: "Software engineer", text: "The dashboard is delightful. Depositing and tracking my Gold plan feels effortless — I finally understand where my capital is at every step." },
    { name: "Bilal M.", role: "Shopify seller", text: "Referral commissions actually pay out on time. I built a small downline in a month and the tiered rewards are transparent." },
    { name: "Sara K.", role: "Investor", text: "Withdrawal windows are respected, KYC was quick, and support responded within an hour. Exactly what I want from a fintech." },
  ];
  return (
    <section className="py-24 sm:py-32 bg-emerald-50/40" data-testid="section-testimonials">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-white border border-emerald-100 px-3 py-1 text-[11px] font-bold text-emerald-700 uppercase tracking-wider">
            <Star className="w-3.5 h-3.5" /> Loved by investors
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-black text-emerald-950 tracking-tight" style={{ fontFamily: "'Sora','Plus Jakarta Sans',system-ui,sans-serif" }}>
            Stories from the community.
          </h2>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {quotes.map((q, i) => (
            <motion.figure
              key={q.name}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08 }}
              className="rounded-2xl border border-emerald-100 bg-white p-6 shadow-sm hover:shadow-lg transition-shadow"
              data-testid={`testimonial-${i}`}
            >
              <div className="flex items-center gap-1 text-amber-400 mb-3">
                {[...Array(5)].map((_, k) => <Star key={k} className="w-4 h-4 fill-current" />)}
              </div>
              <blockquote className="text-sm text-emerald-900/80 leading-relaxed">"{q.text}"</blockquote>
              <figcaption className="mt-5 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center text-white font-black text-sm">
                  {q.name.split(" ").map(s => s[0]).join("")}
                </div>
                <div>
                  <div className="text-sm font-bold text-emerald-950">{q.name}</div>
                  <div className="text-[11px] text-emerald-800/60">{q.role}</div>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-24 sm:py-28" data-testid="section-cta">
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-800 p-10 sm:p-14 text-center shadow-2xl shadow-emerald-500/25">
          <div aria-hidden className="absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.5)_1px,transparent_0)] [background-size:20px_20px]" />
          <div aria-hidden className="absolute -top-10 -left-10 w-56 h-56 bg-white/10 rounded-full blur-3xl" />
          <div aria-hidden className="absolute -bottom-10 -right-10 w-64 h-64 bg-emerald-300/20 rounded-full blur-3xl" />

          <div className="relative">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 backdrop-blur px-3 py-1 text-[11px] font-bold text-emerald-50 uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" /> Ready when you are
            </span>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight" style={{ fontFamily: "'Sora','Plus Jakarta Sans',system-ui,sans-serif" }}>
              Open a T-VENTURES account today.
            </h2>
            <p className="mt-4 text-emerald-50/90 max-w-2xl mx-auto">
              Sign up with a referral code, verify your identity, fund your wallet and start allocating in minutes. No hidden fees, no surprises.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <a href="/register.html" data-testid="final-cta-primary" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-emerald-800 font-bold shadow-lg hover:-translate-y-0.5 transition-transform">
                Create free account <ArrowRight className="w-4 h-4" />
              </a>
              <a href="/login.html" data-testid="final-cta-secondary" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-emerald-800/40 border border-white/20 text-white font-semibold hover:bg-emerald-800/60 transition-colors">
                <Lock className="w-4 h-4" /> Sign in
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-emerald-100 bg-white" data-testid="landing-footer">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-lg shadow-emerald-500/25">
              <img src="/tventures-logo.svg" alt="" className="w-5 h-5" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="tracking-[0.14em] font-extrabold text-emerald-900 text-[15px]">T-VENTURES</span>
              <span className="text-[9px] tracking-[0.28em] text-emerald-600 font-semibold mt-0.5">SECURE FINTECH</span>
            </span>
          </div>
          <p className="mt-5 text-sm text-emerald-900/70 max-w-md leading-relaxed">
            A modern investment workspace with curated plans, transparent reporting and a tiered referral network. Built for investors who value clarity.
          </p>
        </div>
        <div>
          <div className="text-[11px] font-bold text-emerald-700 uppercase tracking-widest">Product</div>
          <ul className="mt-4 space-y-2.5 text-sm">
            <li><a href="#features" className="text-emerald-900/70 hover:text-emerald-700">Features</a></li>
            <li><a href="#plans" className="text-emerald-900/70 hover:text-emerald-700">Plans</a></li>
            <li><a href="#how" className="text-emerald-900/70 hover:text-emerald-700">How it works</a></li>
            <li><a href="#security" className="text-emerald-900/70 hover:text-emerald-700">Security</a></li>
          </ul>
        </div>
        <div>
          <div className="text-[11px] font-bold text-emerald-700 uppercase tracking-widest">Account</div>
          <ul className="mt-4 space-y-2.5 text-sm">
            <li><a href="/login.html" className="text-emerald-900/70 hover:text-emerald-700">Sign in</a></li>
            <li><a href="/register.html" className="text-emerald-900/70 hover:text-emerald-700">Create account</a></li>
            <li><a href="/support.html" className="text-emerald-900/70 hover:text-emerald-700">Support</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-emerald-100">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-emerald-900/60">
          <div>© {new Date().getFullYear()} T-VENTURES. All rights reserved.</div>
          <div className="flex items-center gap-5">
            <a href="/support.html" className="hover:text-emerald-700">Contact</a>
            <span className="opacity-40">·</span>
            <span>Investing involves risk. Past performance is not indicative of future results.</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function App() {
  const loggedIn = useSignedInRedirect();

  return (
    <div className="min-h-screen bg-white text-emerald-950" data-testid="landing-root" style={{ fontFamily: "'Plus Jakarta Sans','Inter',system-ui,sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Sora:wght@600;700;800;900&display=swap" rel="stylesheet" />
      <style>{`
        @keyframes floatA { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(30px,-20px) scale(1.08); } }
        @keyframes floatB { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-25px,20px) scale(1.06); } }
        html { scroll-behavior: smooth; }
        ::selection { background: #10b98133; color: #064e3b; }
      `}</style>
      <Navbar loggedIn={loggedIn} />
      <main>
        <Hero />
        <TrustStrip />
        <Features />
        <PlansPreview />
        <HowItWorks />
        <Security />
        <Testimonials />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}

export default App;
